import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, doc, writeBatch, where, deleteDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function Valhalla() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [activeBooks, setActiveBooks] = useState([]);
  const [archivedBooks, setArchivedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveMode, setArchiveMode] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [viewMode, setViewMode] = useState('active'); // active or archived
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [selectedArchivedBooks, setSelectedArchivedBooks] = useState([]);
  const [restoreMode, setRestoreMode] = useState(false);

  useEffect(() => {
    loadBooks();
  }, [currentUser]);

  useEffect(() => {
    filterBooks();
  }, [activeBooks, archivedBooks, searchTerm, filterStatus, filterLocation, viewMode]);

  // Get unique locations from books
  const locations = React.useMemo(() => {
    const allBooks = [...activeBooks, ...archivedBooks];
    const locationSet = new Set();
    allBooks.forEach(book => {
      if (book.location) locationSet.add(book.location);
    });
    return Array.from(locationSet).sort();
  }, [activeBooks, archivedBooks]);

  const loadBooks = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // Load active books
      const activeBooksRef = collection(db, 'users', currentUser.uid, 'books');
      const activeQuery = query(activeBooksRef, orderBy('createdAt', 'desc'));
      const activeSnapshot = await getDocs(activeQuery);
      
      const activeBooksData = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        archived: false
      }));

      // Load archived books
      const archivedBooksRef = collection(db, 'users', currentUser.uid, 'archives');
      const archivedQuery = query(archivedBooksRef, orderBy('archivedAt', 'desc'));
      const archivedSnapshot = await getDocs(archivedQuery);
      
      const archivedBooksData = archivedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        archivedAt: doc.data().archivedAt?.toDate?.() || new Date(),
        archived: true
      }));

      setActiveBooks(activeBooksData);
      setArchivedBooks(archivedBooksData);
    } catch (error) {
      console.error('Error loading books:', error);
    }
    setLoading(false);
  };

  const filterBooks = () => {
    const booksToFilter = viewMode === 'active' ? activeBooks : archivedBooks;
    let filtered = [...booksToFilter];

    // Search filter - includes location
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(book =>
        book.title?.toLowerCase().includes(term) ||
        book.author?.toLowerCase().includes(term) ||
        book.barcode?.includes(term) ||
        book.callNumber?.toLowerCase().includes(term) ||
        book.location?.toLowerCase().includes(term)
      );
    }

    // Location filter
    if (filterLocation !== 'all') {
      filtered = filtered.filter(book => book.location === filterLocation);
    }

    // Status filter for active books
    if (viewMode === 'active' && filterStatus !== 'all') {
      if (filterStatus === 'ready-to-archive') {
        filtered = filtered.filter(book =>
          book.status === 'exported' ||
          book.status === 'printed' ||
          (book.printedAt && book.exportedAt)
        );
      } else {
        filtered = filtered.filter(book => book.status === filterStatus);
      }
    }

    setFilteredBooks(filtered);
  };

  const archiveBooks = async () => {
    if (selectedBooks.length === 0) return;

    setArchiveMode(true);
    try {
      const batch = writeBatch(db);
      const booksToArchive = activeBooks.filter(book => selectedBooks.includes(book.id));
      
      // Create archived versions (minimal data)
      booksToArchive.forEach(book => {
        const archiveRef = doc(collection(db, 'users', currentUser.uid, 'archives'));
        const archiveData = {
          originalId: book.id,
          title: book.title,
          author: book.author,
          barcode: book.barcode,
          callNumber: book.callNumber,
          location: book.location,
          status: book.status || 'completed',
          catalogedDate: book.catalogedDate || book.createdAt?.toISOString().split('T')[0],
          archivedAt: new Date(),
          price: book.price || 0
        };
        batch.set(archiveRef, archiveData);
        
        // Delete from active collection
        const activeRef = doc(db, 'users', currentUser.uid, 'books', book.id);
        batch.delete(activeRef);
      });
      
      await batch.commit();
      
      alert(`Successfully archived ${selectedBooks.length} books! They've been moved to Valhalla.`);
      setSelectedBooks([]);
      loadBooks();
    } catch (error) {
      console.error('Error archiving books:', error);
      alert('Error archiving books: ' + error.message);
    }
    setArchiveMode(false);
  };

  const restoreBook = async (archivedBook) => {
    try {
      const batch = writeBatch(db);

      // Restore to active books (with full data structure)
      const activeRef = doc(collection(db, 'users', currentUser.uid, 'books'));
      const restoredData = {
        title: archivedBook.title,
        author: archivedBook.author,
        barcode: archivedBook.barcode,
        callNumber: archivedBook.callNumber,
        location: archivedBook.location,
        status: 'cataloged',
        price: archivedBook.price || 0,
        createdAt: new Date(),
        restoredAt: new Date(),
        originallyArchivedAt: archivedBook.archivedAt
      };
      batch.set(activeRef, restoredData);

      // Remove from archives
      const archiveRef = doc(db, 'users', currentUser.uid, 'archives', archivedBook.id);
      batch.delete(archiveRef);

      await batch.commit();

      alert('Book restored to active collection!');
      loadBooks();
    } catch (error) {
      console.error('Error restoring book:', error);
      alert('Error restoring book: ' + error.message);
    }
  };

  const restoreSelectedBooks = async () => {
    if (selectedArchivedBooks.length === 0) return;

    setRestoreMode(true);
    try {
      const batch = writeBatch(db);
      const booksToRestore = archivedBooks.filter(book => selectedArchivedBooks.includes(book.id));

      booksToRestore.forEach(archivedBook => {
        // Restore to active books
        const activeRef = doc(collection(db, 'users', currentUser.uid, 'books'));
        const restoredData = {
          title: archivedBook.title,
          author: archivedBook.author,
          barcode: archivedBook.barcode,
          callNumber: archivedBook.callNumber,
          location: archivedBook.location,
          status: 'cataloged',
          price: archivedBook.price || 0,
          createdAt: new Date(),
          restoredAt: new Date(),
          originallyArchivedAt: archivedBook.archivedAt
        };
        batch.set(activeRef, restoredData);

        // Remove from archives
        const archiveRef = doc(db, 'users', currentUser.uid, 'archives', archivedBook.id);
        batch.delete(archiveRef);
      });

      await batch.commit();

      alert(`Successfully restored ${selectedArchivedBooks.length} books to active collection!`);
      setSelectedArchivedBooks([]);
      loadBooks();
    } catch (error) {
      console.error('Error restoring books:', error);
      alert('Error restoring books: ' + error.message);
    }
    setRestoreMode(false);
  };

  const deleteArchivedBook = async (bookId) => {
    if (!window.confirm('Permanently delete this archived book? This cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'archives', bookId));
      alert('Archived book permanently deleted.');
      loadBooks();
    } catch (error) {
      console.error('Error deleting archived book:', error);
      alert('Error deleting book: ' + error.message);
    }
  };

  const getReadyToArchiveCount = () => {
    return activeBooks.filter(book => 
      book.status === 'exported' || 
      book.status === 'printed' ||
      (book.printedAt && book.exportedAt)
    ).length;
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return date.toLocaleDateString();
  };

  const calculateStorageSavings = () => {
    const activeCount = activeBooks.length;
    const archivedCount = archivedBooks.length;
    const totalCount = activeCount + archivedCount;
    
    if (totalCount === 0) return 0;
    
    // Estimate: archived books use ~20% of storage (minimal fields vs full MARC data)
    const savingsPercentage = Math.round((archivedCount / totalCount) * 80);
    return savingsPercentage;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">Valhalla</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Storage Savings</p>
                <p className="text-xl font-bold">{calculateStorageSavings()}%</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">⚔️ Valhalla</h1>
            <p className="text-slate-600 mb-6">The eternal hall where completed catalog records rest after their noble service</p>
            
            {/* Navigation Links */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/allsight')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                🔍 Browse Active Books
              </button>
              <button
                onClick={() => navigate('/bifrost')}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm"
              >
                🌈 Export MARC
              </button>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-lg p-4 border border-blue-300">
                <p className="text-blue-700 text-sm font-medium">Active Books</p>
                <p className="text-2xl font-bold text-blue-800">{activeBooks.length}</p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg p-4 border border-purple-300">
                <p className="text-purple-700 text-sm font-medium">Archived Books</p>
                <p className="text-2xl font-bold text-purple-800">{archivedBooks.length}</p>
              </div>
              
              <div className="bg-gradient-to-r from-green-100 to-green-200 rounded-lg p-4 border border-green-300">
                <p className="text-green-700 text-sm font-medium">Ready to Archive</p>
                <p className="text-2xl font-bold text-green-800">{getReadyToArchiveCount()}</p>
              </div>
              
              <div className="bg-gradient-to-r from-amber-100 to-amber-200 rounded-lg p-4 border border-amber-300">
                <p className="text-amber-700 text-sm font-medium">Total Cataloged</p>
                <p className="text-2xl font-bold text-amber-800">{activeBooks.length + archivedBooks.length}</p>
              </div>
            </div>
            
            {/* Controls */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  View:
                </label>
                <select
                  value={viewMode}
                  onChange={(e) => {
                    setViewMode(e.target.value);
                    setSelectedArchivedBooks([]);
                    setSelectedBooks([]);
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="active">Active Books</option>
                  <option value="archived">Archived Books</option>
                </select>
              </div>

              {viewMode === 'active' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Status:
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="ready-to-archive">Ready to Archive</option>
                    <option value="exported">Exported</option>
                    <option value="printed">Printed</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Location:
                </label>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Search:
                </label>
                <input
                  type="text"
                  placeholder="Title, author, barcode..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {viewMode === 'active' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Selected: {selectedBooks.length}
                  </label>
                  <button
                    onClick={archiveBooks}
                    disabled={archiveMode || selectedBooks.length === 0}
                    className="w-full bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 font-medium disabled:bg-slate-400 text-sm"
                  >
                    {archiveMode ? 'Archiving...' : '📦 Archive Selected'}
                  </button>
                </div>
              )}

              {viewMode === 'archived' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Selected: {selectedArchivedBooks.length}
                  </label>
                  <button
                    onClick={restoreSelectedBooks}
                    disabled={restoreMode || selectedArchivedBooks.length === 0}
                    className="w-full bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 font-medium disabled:bg-slate-400 text-sm"
                  >
                    {restoreMode ? 'Restoring...' : '⬆️ Restore Selected'}
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Quick Actions:
                </label>
                {viewMode === 'active' ? (
                  <button
                    onClick={() => {
                      const readyIds = activeBooks
                        .filter(book => book.status === 'exported' || book.status === 'printed')
                        .map(book => book.id);
                      setSelectedBooks(readyIds);
                    }}
                    className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm"
                  >
                    Select Ready
                  </button>
                ) : (
                  <button
                    onClick={() => setSelectedArchivedBooks(filteredBooks.map(book => book.id))}
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 text-sm"
                  >
                    Select All Filtered
                  </button>
                )}
              </div>
            </div>

            {/* Archive Instructions */}
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4 border border-amber-200 mb-6">
              <h3 className="font-bold text-amber-800 mb-2">🏛️ How Valhalla Works:</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Archive books after printing labels and exporting MARC records</li>
                <li>• Archived books keep only essential data: title, author, barcode, location, date</li>
                <li>• This frees up active storage while maintaining your catalog history</li>
                <li>• Books can be restored to active status if needed for editing</li>
                <li>• Permanently delete archived books only if you're certain</li>
              </ul>
            </div>
          </div>

          {/* Book List */}
          {loading ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600">Loading {viewMode} books...</p>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600 mb-4">
                {viewMode === 'active' 
                  ? (activeBooks.length === 0 
                      ? 'No active books found. Visit the Forge to start cataloging!' 
                      : 'No books match your filter criteria.')
                  : (archivedBooks.length === 0 
                      ? 'No archived books yet. Complete some catalog workflows first!' 
                      : 'No archived books match your search.')
                }
              </p>
              {activeBooks.length === 0 && (
                <button 
                  onClick={() => navigate('/forge')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
                >
                  Start Cataloging
                </button>
              )}
            </div>
          ) : (
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
              <div className="px-6 py-4 border-b border-slate-300 flex justify-between items-center bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">
                  {viewMode === 'active' ? 'Active Books' : 'Archived Books'} ({filteredBooks.length})
                </h2>
                <div className="space-x-2">
                  <button
                    onClick={() => viewMode === 'active'
                      ? setSelectedBooks(filteredBooks.map(book => book.id))
                      : setSelectedArchivedBooks(filteredBooks.map(book => book.id))
                    }
                    className="text-purple-600 hover:text-purple-500 text-sm"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => viewMode === 'active'
                      ? setSelectedBooks([])
                      : setSelectedArchivedBooks([])
                    }
                    className="text-red-600 hover:text-red-500 text-sm"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
              <div className="divide-y divide-slate-300 max-h-96 overflow-y-auto">
                {filteredBooks.map((book) => (
                  <div key={book.id} className="px-6 py-3 hover:bg-slate-150">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={viewMode === 'active'
                            ? selectedBooks.includes(book.id)
                            : selectedArchivedBooks.includes(book.id)
                          }
                          onChange={(e) => {
                            if (viewMode === 'active') {
                              if (e.target.checked) {
                                setSelectedBooks([...selectedBooks, book.id]);
                              } else {
                                setSelectedBooks(selectedBooks.filter(id => id !== book.id));
                              }
                            } else {
                              if (e.target.checked) {
                                setSelectedArchivedBooks([...selectedArchivedBooks, book.id]);
                              } else {
                                setSelectedArchivedBooks(selectedArchivedBooks.filter(id => id !== book.id));
                              }
                            }
                          }}
                          className="h-4 w-4 text-purple-600"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="font-medium text-slate-800">{book.title}</h3>
                            {book.status && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                book.status === 'exported' ? 'bg-purple-100 text-purple-800' :
                                book.status === 'printed' ? 'bg-green-100 text-green-800' :
                                book.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {book.status}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600">
                            <span>{book.author} • </span>
                            <span className="font-mono">{book.barcode} • </span>
                            <span className="font-mono">{book.callNumber} • </span>
                            <span>{book.location}</span>
                            {book.archived && (
                              <span className="text-slate-500"> • Archived {formatDate(book.archivedAt)}</span>
                            )}
                            {!book.archived && book.createdAt && (
                              <span className="text-slate-500"> • Added {formatDate(book.createdAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {viewMode === 'archived' ? (
                          <>
                            <button
                              onClick={() => restoreBook(book)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Restore
                            </button>
                            <button
                              onClick={() => deleteArchivedBook(book.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-500">
                            {book.status === 'exported' || book.status === 'printed' ? 'Ready' : 'In Progress'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Valhalla;